import { supabase } from "@/integrations/supabase/client";

export interface AppNotification {
  id: string;
  title: string;
  body: string;
  type: string;
  data: Record<string, unknown>;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  sourceType: string | null;
  sourceId: string | null;
}

export interface CheckNotificationInput {
  id: string;
  projectName?: string;
  description?: string;
  amount?: number;
  dueDate?: string;
  dueInDays?: number;
  isGivenCheck?: boolean;
}

const toTitleByDueDays = (dueInDays?: number) => {
  if (typeof dueInDays !== "number") return "Yaklaşan çek ödemesi";
  if (dueInDays < 0) return "Çek ödemesi gecikti";
  if (dueInDays === 0) return "Çek ödemesi bugün";
  if (dueInDays === 1) return "Çek ödemesi yarın";
  return "Yaklaşan çek ödemesi";
};

export const notificationsAPI = {
  async getNotifications(limit = 30): Promise<AppNotification[]> {
    const { data, error } = await supabase
      .from("notifications")
      .select("id, title, body, type, data, is_read, read_at, created_at, source_type, source_id")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      throw error;
    }

    return (data ?? []).map((row) => ({
      id: row.id,
      title: row.title,
      body: row.body,
      type: row.type,
      data: (row.data as Record<string, unknown>) ?? {},
      isRead: row.is_read,
      readAt: row.read_at,
      createdAt: row.created_at,
      sourceType: row.source_type,
      sourceId: row.source_id,
    }));
  },

  async getUnreadCount(): Promise<number> {
    const { count, error } = await supabase
      .from("notifications")
      .select("id", { count: "exact", head: true })
      .eq("is_read", false);

    if (error) {
      throw error;
    }

    return count ?? 0;
  },

  async markNotificationRead(notificationId: string): Promise<boolean> {
    const { error } = await supabase
      .from("notifications")
      .update({
        is_read: true,
        read_at: new Date().toISOString(),
      })
      .eq("id", notificationId)
      .eq("is_read", false);

    if (error) {
      throw error;
    }

    return true;
  },

  async syncCheckReminderNotifications(
    userId: string,
    checks: CheckNotificationInput[]
  ): Promise<void> {
    if (!userId || checks.length === 0) return;

    const rows = checks.map((check) => {
      const sourceId = `${check.id}:${check.dueDate ?? "unknown"}`;
      const title = toTitleByDueDays(check.dueInDays);
      const body = [check.projectName, check.description].filter(Boolean).join(" • ") || "Çek ödemesi";

      return {
        user_id: userId,
        title,
        body,
        type: "check_payment",
        source_type: "check_payment",
        source_id: sourceId,
        data: {
          checkId: check.id,
          projectName: check.projectName ?? null,
          description: check.description ?? null,
          amount: check.amount ?? null,
          dueDate: check.dueDate ?? null,
          dueInDays: check.dueInDays ?? null,
          isGivenCheck: check.isGivenCheck ?? null,
        },
      };
    });

    const { error } = await supabase
      .from("notifications")
      .upsert(rows, { onConflict: "user_id,source_type,source_id" });

    if (error) {
      throw error;
    }
  },
};
